import { useQuery } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import { useAuthStore } from '../store/authStore'
import { useEnforcement } from './useEnforcement'
import { resolveEntitlementPlan } from '../lib/planEntitlement'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const db = (t: string): any => (supabase as any).from(t)

interface PlanLimits {
  max_users: number      // -1 = unlimited
  max_locations: number
  max_products: number
}

interface Usage {
  users: number
  locations: number
  products: number
}

export function usePlanLimits() {
  const { tenant } = useAuthStore()
  const { enforced } = useEnforcement()

  const planId = tenant?.plan
  const tenantId = tenant?.id
  // SUB.4: trial resolves to the top tier's entitlements (Pro), not a no-row special
  // case. Real plans resolve to themselves; unknown plans pass through unchanged.
  const effectivePlan = resolveEntitlementPlan(planId)

  // Read limits from subscription_plans (the real source), keyed by the effective plan.
  const { data: limits, isLoading: loadingLimits } = useQuery({
    queryKey: ['plan-limits', effectivePlan],
    enabled: !!effectivePlan,
    queryFn: async () => {
      const { data, error } = await db('subscription_plans')
        .select('max_users, max_locations, max_products')
        .eq('id', effectivePlan)
        .single()
      if (error) throw error
      return data as PlanLimits
    },
  })

  // Count current usage from real tables — predicates MUST match migration 181.
  const { data: usage, isLoading: loadingUsage } = useQuery({
    queryKey: ['plan-usage', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const [users, locs, prods] = await Promise.all([
        db('tenant_users').select('id', { count: 'exact', head: true })
          .eq('tenant_id', tenantId).eq('is_active', true),
        db('warehouses').select('id', { count: 'exact', head: true })
          .eq('tenant_id', tenantId).is('archived_at', null),
        db('products').select('id', { count: 'exact', head: true })
          .eq('tenant_id', tenantId).eq('is_active', true),
      ])
      return {
        users: users.count ?? 0,
        locations: locs.count ?? 0,
        products: prods.count ?? 0,
      } as Usage
    },
  })

  // canAddX: if NOT enforced, always true. If no limits known (trial), always
  // true (fail-open). If unlimited (-1), always true. Else compare.
  const canAdd = (current: number, max: number | undefined | null): boolean => {
    if (!enforced) return true
    if (max === undefined || max === null) return true  // unknown
    if (max === -1) return true  // unlimited
    return current < max
  }

  return {
    limits,
    usage,
    enforced,
    canAddUser: canAdd(usage?.users ?? 0, limits?.max_users),
    canAddLocation: canAdd(usage?.locations ?? 0, limits?.max_locations),
    canAddProduct: canAdd(usage?.products ?? 0, limits?.max_products),
    remaining: limits && usage ? {
      users: limits.max_users === -1 ? Infinity : Math.max(0, limits.max_users - usage.users),
      locations: limits.max_locations === -1 ? Infinity : Math.max(0, limits.max_locations - usage.locations),
      products: limits.max_products === -1 ? Infinity : Math.max(0, limits.max_products - usage.products),
    } : null,
    isLoading: loadingLimits || loadingUsage,
  }
}

// ── Display helpers (consumed by PlanLimitBar + page headers) ────────────────

export function formatLimitLabel(used: number, max: number | undefined | null): string {
  if (max === undefined || max === null || max === -1) return `${used}`
  return `${used} of ${max}`
}

export function getLimitPercent(used: number, max: number): number {
  if (max === -1 || max === 0) return 0
  return Math.min(100, Math.round((used / max) * 100))
}
